import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  Table, Button, Icon,
  Label, Popup, Progress, TextArea,
} from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { LoadingSpinner, BasicModal, FileDropzone, ConfirmDialog, EvidenceTextButton } from "../../../Shared";
import { buildStandardDashboardPdf, drawPill, drawProgressCell, SEMANTIC_HEX } from "../../../Shared/pdfCharts";
import {
  LegalRequirement, Area, Source, Authority,
  Frequency, ActionPlan, Site, Company, Requirement,
} from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage, useSite, useCompany } from "../../../../contexts";
import { isAdmin, isMaster } from "../../../../utils/checkPermission";
import { AreaForm } from "../../Areas/AreaForm";
import { SourceForm } from "../../Sources/SourceForm";
import { FrequencyForm } from "../../Frequencies/FrequencyForm";
import { AuthorityForm } from "../../Authorities/AuthorityForm";
import { RequirementForm } from "../../Requirements/RequirementForm";
import { LegalRequirementDates } from "../LegalRequirementDates";
import "./LegalRequirementsTable.scss";

const lrController        = new LegalRequirement();
const actionPlanController = new ActionPlan();
const areaController      = new Area();
const sourceController    = new Source();
const authorityController = new Authority();
const frequencyController = new Frequency();
const siteCtrl            = new Site();
const companyCtrl         = new Company();
const requirementCtrl     = new Requirement();

const EVIDENCE_TYPE_OPTIONS = [
  { key: "licencia",           value: "licencia",           text: "Licencia" },
  { key: "permiso",            value: "permiso",            text: "Permiso" },
  { key: "informe",            value: "informe",            text: "Informe / Análisis / Monitoreo" },
  { key: "registros_firmados", value: "registros_firmados", text: "Registros firmados" },
  { key: "ddjj",               value: "ddjj",               text: "DDJJ — Declaración jurada" },
  { key: "contrato_servicio",  value: "contrato_servicio",  text: "Contrato de servicio vigente" },
  { key: "certificado",        value: "certificado",        text: "Certificado profesional" },
  { key: "constancia_dc3",     value: "constancia_dc3",     text: "Constancia DC-3 (MEX)" },
  { key: "acta_inspeccion",    value: "acta_inspeccion",    text: "Acta de inspección" },
  { key: "fotografia",         value: "fotografia",         text: "Fotografía / evidencia visual" },
  { key: "otro",               value: "otro",               text: "Otro" },
];


const STATUS_COLOR = { compliant: "green", partial: "orange", non_compliant: "red", pending: "yellow" };

const EMPTY = {
  area: "", source: "", authority: "", requirement: "",
  responsible: "", frequency: "", site: "",
  expiration: "", status: "", progress: 0, observations: [],
  evidenceTypes: [], occurrences: [],
};

const AVATAR_COLORS = ["#1A5FA0", "#0E376E", "#3D6B9E", "#648EBC", "#4F7FB5", "#2A4F80", "#8AB3D4"];

function LRCatalogModal({ open, title, onClose, children }) {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#F8F9FB", borderRadius: "20px", padding: "30px 32px 26px", width: "min(420px, 90vw)", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>
        <p style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1A2E4A", fontFamily: "'Archivo', sans-serif", margin: "0 0 20px" }}>{title}</p>
        {children}
      </div>
    </div>,
    document.body
  );
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function formatDate(date) {
  if (!date) return "-";
  const str = typeof date === "string" ? date : date.toISOString();
  const [y, m, d] = str.split("T")[0].split("-");
  return `${d}/${m}/${y}`;
}

function getAlertLevel(item) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const warn30 = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
  const nextOccDate = (item.occurrences || [])
    .filter((o) => !o.completed && o.date)
    .map((o) => new Date(o.date))
    .sort((a, b) => a - b)[0];
  const date = nextOccDate ?? (item.expiration ? new Date(item.expiration) : null);
  if (!date || isNaN(date)) return null;
  if (date < today) return "expired";
  if (date <= warn30) return "warning";
  return null;
}

function EvidencePopup({ value = [], onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => {
    const next = value.includes(v) ? value.filter((x) => x !== v) : [...value, v];
    onChange(next);
  };
  const label = value.length === 0 ? "—"
    : value.length === 1 ? (EVIDENCE_TYPE_OPTIONS.find((o) => o.value === value[0])?.text || value[0])
    : value.length === 2 ? value.map((v) => EVIDENCE_TYPE_OPTIONS.find((o) => o.value === v)?.text || v).join(", ")
    : "Varios";

  return (
    <Popup
      open={open}
      onClose={() => setOpen(false)}
      on="click"
      position="bottom center"
      trigger={
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          className="lr-table__ev-btn"
        >
          {label} <Icon name="caret down" style={{ marginLeft: 4 }} />
        </button>
      }
      content={
        <div className="lr-table__ev-popup">
          {EVIDENCE_TYPE_OPTIONS.map((o) => (
            <label key={o.value} className="lr-table__ev-option">
              <input
                type="checkbox"
                checked={value.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              <span>{o.text}</span>
            </label>
          ))}
        </div>
      }
    />
  );
}

const FREQ_CONFIG = {
  "diario":        { count: 365, addMonths: 0, addDays: 1 },
  "semanal":       { count: 52,  addMonths: 0, addDays: 7 },
  "quincenal":     { count: 24,  addMonths: 0, addDays: 15 },
  "mensual":       { count: 12,  addMonths: 1, addDays: 0 },
  "bimestral":     { count: 6,   addMonths: 2, addDays: 0 },
  "trimestral":    { count: 4,   addMonths: 3, addDays: 0 },
  "cuatrimestral": { count: 3,   addMonths: 4, addDays: 0 },
  "semestral":     { count: 2,   addMonths: 6, addDays: 0 },
  "anual":         { count: 1,   addMonths: 12, addDays: 0 },
};

function freqAddInterval(date, config) {
  const d = new Date(date);
  if (config.addMonths) d.setMonth(d.getMonth() + config.addMonths);
  else d.setDate(d.getDate() + config.addDays);
  return d;
}

function toDateStr(date) {
  if (!date) return "";
  const d = new Date(date);
  return isNaN(d) ? "" : d.toISOString().split("T")[0];
}

function regenerateOccurrences(freqName, startDateStr, existingOccurrences) {
  const freqConfig = FREQ_CONFIG[freqName?.toLowerCase().trim()];
  if (!freqConfig || !startDateStr) return [];
  const existingByDate = {};
  (existingOccurrences || []).forEach((o) => { existingByDate[o.date] = o; });
  const result = [];
  let current = new Date(startDateStr + "T12:00:00");
  for (let i = 0; i < freqConfig.count; i++) {
    const ds = toDateStr(current);
    result.push(existingByDate[ds] || { date: ds, completed: false, comments: [], files: [] });
    current = freqAddInterval(current, freqConfig);
  }
  return result;
}

const InlineSelect = ({ options, value, onChange, placeholder, addLabel, onAdd }) => (
  <select
    value={value || ""}
    onChange={(e) => {
      if (onAdd && e.target.value === "__add__") { onAdd(); }
      else onChange(e.target.value);
    }}
    className="lr-table__inline-select"
  >
    <option value="">{placeholder || "-"}</option>
    {options.map((o) => (
      <option key={o.key} value={o.value}>{o.text}</option>
    ))}
    {addLabel && <option value="__add__">{addLabel}</option>}
  </select>
);

const FilterDropdown = ({ options, value, onChange, placeholder, addLabel, onAdd }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="lr-fd" ref={ref}>
      <button type="button" className="lr-fd-toggle" onClick={() => setOpen((v) => !v)}>
        <span className={selected ? "" : "lr-fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        <span className="lr-fd-icons">
          {selected && (
            <span
              className="lr-fd-clear"
              onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
              title={placeholder}
            >
              ✕
            </span>
          )}
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`lr-fd-arrow${open ? " lr-fd-arrow--open" : ""}`}>
            <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="lr-fd-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`lr-fd-item${o.value === value ? " lr-fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
          {addLabel && (
            <button
              type="button"
              className="lr-fd-item lr-fd-item--add"
              onClick={() => { onAdd && onAdd(); setOpen(false); }}
            >
              {addLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export function LegalRequirementsTable(props) {
  const { reload, onReload, extSearch = "", extResponsible = "", extRequirement = "", extStatus = "", extYear = null, role } = props;
  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const { site: siteFromContext } = useSite();
  const { company: companyFromContext } = useCompany();
  const t = (key) => translations[key] || key;
  const [searchParams, setSearchParams] = useSearchParams();

  const STATUS_FILTER_OPTIONS = [
    { key: "all",       value: "",          text: t("all_statuses") },
    { key: "compliant",     value: "compliant",     text: t("status_cumple") },
    { key: "partial",       value: "partial",       text: t("status_parcial") },
    { key: "non_compliant", value: "non_compliant", text: t("status_no_cumple") },
    { key: "pending",       value: "pending",       text: t("status_pendiente") },
  ];

  const [items,        setItems]        = useState(null);
  const [itemsFilter,  setItemsFilter]  = useState([]);
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [areaFilter,   setAreaFilter]   = useState("");
  const [siteFilter,      setSiteFilter]      = useState("");
  const [sites,           setSites]           = useState([]);
  const [companySiteIds,  setCompanySiteIds]  = useState([]);
  const [companyName,     setCompanyName]     = useState("");
  const noSiteInCtx    = !siteFromContext    || siteFromContext    === "0" || siteFromContext    === 0;
  const noCompanyInCtx = !companyFromContext || companyFromContext === "0" || companyFromContext === 0;
  const [areaOptions,  setAreaOptions]  = useState([]);

  const canTransfer = isAdmin(role) || isMaster(role);
  const totalCols = canTransfer ? 11 : 10;
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferYear, setTransferYear] = useState((extYear || new Date().getFullYear()) + 1);
  const [transferring, setTransferring] = useState(false);
  const [openRowMenu, setOpenRowMenu] = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!openRowMenu) return;
    const handler = (e) => { if (!e.target.closest(".lr-table__row-menu, .lr-table__row-menu-list")) setOpenRowMenu(null); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openRowMenu]);

  const toggleRowMenu = (id, e) => {
    if (openRowMenu?.id === id) { setOpenRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 340;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setOpenRowMenu({
      id,
      left: rect.right - 190,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === itemsFilter.length && itemsFilter.length > 0) return new Set();
      return new Set(itemsFilter.map((i) => i._id));
    });
  };

  const openTransfer = () => {
    setTransferYear((extYear || new Date().getFullYear()) + 1);
    setShowTransfer(true);
  };

  const transferInFlightRef = useRef(false);
  const handleTransfer = async () => {
    if (transferInFlightRef.current) return;
    transferInFlightRef.current = true;
    setTransferring(true);
    try {
      const result = await lrController.copyYear(accessToken, Array.from(selectedIds), transferYear);
      toast.success(`${t("lr_transfer_success")}: ${result.created} ${t("lr_transfer_created")}, ${result.skipped} ${t("lr_transfer_skipped")}`);
      setShowTransfer(false);
      setSelectedIds(new Set());
      onReload();
    } catch {
      toast.error(t("lr_transfer_error"));
    } finally {
      transferInFlightRef.current = false;
      setTransferring(false);
    }
  };

  // Catalogs for inline selects
  const [catAreas,       setCatAreas]       = useState([]);
  const [catReqs,        setCatReqs]        = useState([]);
  const [catSources,     setCatSources]     = useState([]);
  const [catAuthorities, setCatAuthorities] = useState([]);
  const [catFrequencies, setCatFrequencies] = useState([]);

  // Inline edit
  const [editingId,   setEditingId]   = useState(null); // null | "new" | _id
  const [editingData, setEditingData] = useState(EMPTY);
  const [saving,      setSaving]      = useState(false);

  // Modales para nuevos catálogos
  const [showAreaModal,      setShowAreaModal]      = useState(false);
  const [showSourceModal,    setShowSourceModal]    = useState(false);
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [showAuthorityModal, setShowAuthorityModal] = useState(false);

  const reloadAreas = async (selectNew = false) => {
    try {
      const toOpts = (arr) => arr.map((x) => ({ key: x._id, value: x._id, text: x.name }));
      const prevIds = catAreas.map((a) => a.key);
      const fetched = await areaController.getAreas(accessToken);
      const opts = toOpts(fetched);
      setCatAreas(opts);
      setAreaOptions([{ key: "all", value: "", text: "Todas las áreas" }, ...opts]);
      if (selectNew) {
        const added = opts.find((o) => !prevIds.includes(o.key));
        if (added) setField("area", added.value);
      }
    } catch {}
  };

  const reloadSources = async (selectNew = false) => {
    try {
      const toOpts = (arr) => arr.map((x) => ({ key: x._id, value: x._id, text: x.name }));
      const prevIds = catSources.map((s) => s.key);
      const fetched = await sourceController.getSources(accessToken);
      const opts = toOpts(fetched);
      setCatSources(opts);
      if (selectNew) {
        const added = opts.find((o) => !prevIds.includes(o.key));
        if (added) setField("source", added.value);
      }
    } catch {}
  };

  const reloadAuthorities = async (selectNew = false) => {
    try {
      const toOpts = (arr) => arr.map((x) => ({ key: x._id, value: x._id, text: x.name }));
      const prevIds = catAuthorities.map((a) => a.key);
      const fetched = await authorityController.getAuthorities(accessToken);
      const opts = toOpts(fetched);
      setCatAuthorities(opts);
      if (selectNew) {
        const added = opts.find((o) => !prevIds.includes(o.key));
        if (added) setField("authority", added.value);
      }
    } catch {}
  };

  const reloadFrequencies = async (selectNew = false) => {
    try {
      const toOpts = (arr) => arr.map((x) => ({ key: x._id, value: x._id, text: x.name }));
      const prevIds = catFrequencies.map((f) => f.key);
      const fetched = await frequencyController.getFrequencies(accessToken);
      const opts = toOpts(fetched);
      setCatFrequencies(opts);
      if (selectNew) {
        const added = opts.find((o) => !prevIds.includes(o.key));
        if (added) setField("frequency", added.value);
      }
    } catch {}
  };
  const reloadRequirements = async (selectNew = false) => {
  try {
    const toOpts = (arr) => arr.map((x) => ({ key: x._id, value: x._id, text: x.name }));
    const prevIds = catReqs.map((a) => a.key);
    const fetched = await requirementCtrl.getRequirements(accessToken);
    const opts = toOpts(fetched);
    setCatReqs(opts);
    if (selectNew) {
      const added = opts.find((o) => !prevIds.includes(o.key));
      if (added) setField("requirement", added.value);
    }
  } catch {}
};


  // Modal states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [showDates,    setShowDates]    = useState(false);
  const [showObs,      setShowObs]      = useState(false);
  const [showFiles,    setShowFiles]    = useState(false);
  const [showAttach,   setShowAttach]   = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [showPlan,        setShowPlan]        = useState(false);
  const [linkedPlan,      setLinkedPlan]      = useState(null);
  const [showEscalation,  setShowEscalation]  = useState(false);
  const [escalationForm,  setEscalationForm]  = useState(null);
  const [escalationSaving, setEscalationSaving] = useState(false);
  const [planTitle,    setPlanTitle]    = useState("");
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [progressVal,  setProgressVal]  = useState(0);
  const [obsVal,       setObsVal]       = useState("");
  const [editingObsIdx,  setEditingObsIdx]  = useState(null);
  const [editingObsVal,  setEditingObsVal]  = useState("");
  const [editingObsDate, setEditingObsDate] = useState("");
  const [uploading,    setUploading]    = useState(false);
  const [deletingFile, setDeletingFile] = useState(null); // key being deleted
  const [previewUrl,   setPreviewUrl]   = useState(null);
  const [previewName,  setPreviewName]  = useState("");

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!accessToken) return;
    const load = !noCompanyInCtx
      ? siteCtrl.getSitesByCompany(accessToken, companyFromContext, true)
      : siteCtrl.getSites(accessToken, true);
    load.then(res => setSites(Array.isArray(res) ? res : [])).catch(() => {});
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  useEffect(() => { setSiteFilter(""); }, [siteFromContext]);

  useEffect(() => {
    if (noCompanyInCtx) { setCompanySiteIds([]); return; }
    siteCtrl.getSitesByCompany(accessToken, companyFromContext, true)
      .then(res => setCompanySiteIds((res || []).map(s => s._id)))
      .catch(() => setCompanySiteIds([]));
  }, [companyFromContext, accessToken]);

  useEffect(() => {
    if (noCompanyInCtx) { setCompanyName(""); return; }
    companyCtrl.getCompany(accessToken, companyFromContext)
      .then(res => setCompanyName(res?.name || ""))
      .catch(() => setCompanyName(""));
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  // Load all catalogs once
  useEffect(() => {
    const toOpts = (arr) => arr.map((x) => ({ key: x._id, value: x._id, text: x.name }));
    (async () => {
      try {
        const [a, s, au, fr, rq] = await Promise.allSettled([
          areaController.getAreas(accessToken),
          sourceController.getSources(accessToken),
          authorityController.getAuthorities(accessToken),
          frequencyController.getFrequencies(accessToken),
          requirementCtrl.getRequirements(accessToken),
        ]);
        const areas = a.status === "fulfilled" ? toOpts(a.value) : [];
        setCatAreas(areas);
        setCatSources(     s.status  === "fulfilled" ? toOpts(s.value)  : []);
        setCatAuthorities( au.status === "fulfilled" ? toOpts(au.value) : []);
        setCatFrequencies( fr.status === "fulfilled" ? toOpts(fr.value) : []);
        setCatReqs(        rq.status === "fulfilled" ? toOpts(rq.value) : []);
        setAreaOptions([
          { key: "all", value: "", text: t("all_areas") },
          ...areas,
        ]);
      } catch {}
    })();
  }, []);

  // Load data
  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const filters = {};
        if (areaFilter) filters.area = areaFilter;
        const res = await lrController.getLegalRequirements(accessToken, filters);
        setItems(res);
        setItemsFilter(res);
      } catch {
        setItems([]);
        setItemsFilter([]);
      }
    })();
  }, [reload, areaFilter]);

  // Abrir un registro puntual al llegar con ?open=<id> (ej. desde el buscador
  // de evidencias, "Ver documento").
  useEffect(() => {
    if (!items) return;
    const openId = searchParams.get("open");
    if (!openId) return;
    const target = items.find((i) => i._id === openId);
    if (target) startEdit(target);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [items, searchParams]);

  // Client-side filter
  useEffect(() => {
    if (!items) return;
    let filtered = [...items];

    // Year filter (from dashboard)
    if (extYear) {
      filtered = filtered.filter((item) => {
        if (item.year !== undefined && item.year !== null) return item.year === extYear;
        if (item.expiration) return new Date(item.expiration).getFullYear() === extYear;
        return new Date(item.createdAt).getFullYear() === extYear;
      });
    }

    // Search: external takes priority, fallback to internal
    const activeSearch = extSearch || search;
    if (activeSearch) {
      const q = activeSearch.toLowerCase();
      filtered = filtered.filter((item) =>
        item.requirement?.name?.toLowerCase().includes(q) ||
        item.area?.name?.toLowerCase().includes(q) ||
        item.authority?.name?.toLowerCase().includes(q) ||
        item.responsible?.toLowerCase().includes(q) ||
        item.status?.toLowerCase().includes(q) ||
        item.observations?.some((o) => o.text?.toLowerCase().includes(q))
      );
    }

    const activeStatus = extStatus || statusFilter;
    if (activeStatus) filtered = filtered.filter((item) => item.status === activeStatus);

    if (extResponsible) {
      filtered = filtered.filter((item) => (item.responsible || "") === extResponsible);
    }
    if (extRequirement) {
      filtered = filtered.filter((item) => {
        const id = item.requirement?._id || item.requirement || "";
        return id === extRequirement;
      });
    }
    if (!noSiteInCtx) {
      filtered = filtered.filter((item) => (item.site?._id || item.site) === siteFromContext);
    } else if (!noCompanyInCtx && companySiteIds.length > 0) {
      filtered = filtered.filter((item) => companySiteIds.includes(item.site?._id || item.site));
      if (siteFilter) filtered = filtered.filter((item) => (item.site?._id || item.site) === siteFilter);
    } else if (siteFilter) {
      filtered = filtered.filter((item) => (item.site?._id || item.site) === siteFilter);
    }
    setItemsFilter(filtered);
  }, [search, statusFilter, extSearch, extStatus, extResponsible, extRequirement, extYear, siteFilter, siteFromContext, noSiteInCtx, noCompanyInCtx, companySiteIds, items]);

  useEffect(() => { setSelectedIds(new Set()); }, [extYear, reload]);

  const handleExportXLSX = () => {
    if (!itemsFilter.length) return;
    const rows = itemsFilter.map((item, i) => ({
      "N°":           i + 1,
      "Área":         item.area?.name || "-",
      "Fuente":       item.source?.name || "-",
      "Autoridad":    item.authority?.name || "-",
      "Estado":       item.status || "-",
      "Responsable":  item.responsible || "-",
      "Sitio":        item.site?.name || "-",
      "Frecuencia":   item.frequency?.name || "-",
      "Vencimiento":  item.expiration ? formatDate(item.expiration) : "-",
      "Progreso (%)": item.progress ?? 0,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Requisitos Legales");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "requisitos_legales.xlsx");
  };

  const lrExportRows = () => itemsFilter.map((item, i) => ({
    "N°":           i + 1,
    "Área":         item.area?.name || "-",
    "Fuente":       item.source?.name || "-",
    "Autoridad":    item.authority?.name || "-",
    "Estado":       item.status || "-",
    "Responsable":  item.responsible || "-",
    "Sitio":        item.site?.name || "-",
    "Frecuencia":   item.frequency?.name || "-",
    "Vencimiento":  item.expiration ? formatDate(item.expiration) : "-",
    "Progreso (%)": item.progress ?? 0,
  }));

  const handleExportCSV = () => {
    const data = lrExportRows();
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csv = [headers.join(","), ...data.map((r) => headers.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(","))].join("\n");
    saveAs(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }), "requisitos_legales.csv");
  };

  const handleExportPDF = async () => {
    if (!itemsFilter.length) return;
    const siteName = !noSiteInCtx
      ? sites.find(s => s._id === siteFromContext)?.name
      : siteFilter ? sites.find(s => s._id === siteFilter)?.name : null;

    const tableColumns = [
      ...(noSiteInCtx ? [{ key: "site", header: t("site"), flex: 1, value: (r) => r.site?.name || "-" }] : []),
      { key: "area", header: t("area"), flex: 1.1, value: (r) => r.area?.name || "-" },
      { key: "source", header: t("source"), flex: 1.1, value: (r) => r.source?.name || "-" },
      { key: "authority", header: t("authority"), flex: 1.1, value: (r) => r.authority?.name || "-" },
      {
        key: "status", header: t("estado"), flex: 1, align: "center",
        render: (doc, { x, y, width, height, row }) => {
          if (!row.status) return;
          drawPill(doc, { x: x + width / 2, y: y + height / 2, text: STATUS_FILTER_OPTIONS.find((o) => o.value === row.status)?.text || row.status, color: SEMANTIC_HEX[STATUS_COLOR[row.status]] || SEMANTIC_HEX.grey });
        },
      },
      { key: "frequency", header: t("frequency"), flex: 1, value: (r) => r.frequency?.name || "-" },
      { key: "expiration", header: t("expiration"), flex: 1, value: (r) => (r.expiration ? formatDate(r.expiration) : "-") },
      { key: "responsible", header: t("responsible"), flex: 1, value: (r) => r.responsible || "-" },
      {
        key: "progress", header: t("advance"), flex: 1.2,
        render: (doc, { x, y, width, height, row }) => {
          drawProgressCell(doc, { x, y, width, height, pct: row.progress || 0 });
        },
      },
    ];

    await buildStandardDashboardPdf({
      jsPDFCtor: jsPDF,
      pageTitle: t("legal_requirements") || "Requisitos Legales",
      companyName: [companyName, siteName].filter(Boolean).join(" · "),
      generatedOnLabel: t("generated_on"),
      table: { columns: tableColumns, rows: itemsFilter, rowHeight: 24, headerHeight: 22, emptyMessage: t("no_results") },
      tableTitle: t("legal_requirements") || "Requisitos Legales",
      filename: "requisitos_legales.pdf",
    });
  };

  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target))
        setShowExportMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showExportMenu]);

  const getId = (val) => (val && typeof val === "object" ? val._id : val) || "";

  const startNew = () => {
    setEditingId("new");
    setEditingData({ ...EMPTY, site: siteFromContext || "" });
  };

  const startEdit = (item) => {
    setEditingId(item._id);
    setEditingData({
      area:          getId(item.area),
      source:        getId(item.source),
      authority:     getId(item.authority),
      requirement:   getId(item.requirement),
      responsible:   item.responsible || "",
      frequency:     getId(item.frequency),
      site:          getId(item.site),
      createdAt:     item.createdAt || "",
      expiration:    item.expiration ? item.expiration.split("T")[0] : "",
      status:        item.status        || "",
      progress:      item.progress      ?? 0,
      observations:  item.observations  || [],
      evidenceTypes: item.evidenceTypes || [],
      occurrences:   item.occurrences   || [],
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditingData(EMPTY); };

  const setField = (field, value) => setEditingData((prev) => ({ ...prev, [field]: value }));

  const handleSaveEdit = async () => {
    if (!editingData.area || !editingData.site) {
      toast.warning(t("area_and_site_required"));
      return;
    }
    setSaving(true);
    try {
      const refFields = ["area","source","authority","frequency","site","requirement"];
      const payload = { ...editingData };
      refFields.forEach((f) => { if (!payload[f]) delete payload[f]; });
      if (!payload.expiration) delete payload.expiration;
      if (!payload.status)     delete payload.status;

      if (editingId === "new") {
        await lrController.createLegalRequirement(accessToken, payload);
        toast.success(t("legal_requirement_created"));
      } else {
        await lrController.updateLegalRequirement(accessToken, editingId, payload);
        toast.success(t("legal_requirement_updated"));
      }
      cancelEdit();
      onReload();
    } catch (err) {
      toast.error(err?.msg || t("error_saving"));
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        await lrController.uploadFile(accessToken, selectedItem._id, formData);
      }
      toast.success(t("files_uploaded"));
      setShowAttach(false);
      onReload();
    } catch { toast.error(t("error_uploading_files")); }
    finally { setUploading(false); }
  };

  const handleDeleteFile = async (fileKey) => {
    setDeletingFile(fileKey);
    try {
      await lrController.deleteFile(accessToken, selectedItem._id, fileKey);
      toast.success(t("file_deleted"));
      setSelectedItem((prev) => ({ ...prev, files: prev.files.filter((f) => f.key !== fileKey) }));
      onReload();
    } catch { toast.error(t("error_deleting")); }
    finally { setDeletingFile(null); }
  };

  const handleViewFile = async (fileKey, fileName) => {
    try {
      const blobUrl = await lrController.getFile(accessToken, fileKey);
      if (!blobUrl) { toast.error(t("error_downloading_file")); return; }
      if (isImage(fileName)) {
        setPreviewUrl(blobUrl);
        setPreviewName(fileName);
      } else {
        window.open(blobUrl, "_blank");
      }
    } catch { toast.error(t("error_downloading_file")); }
  };

  const isImage = (name) => /\.(jpg|jpeg|png|gif|webp)$/i.test(name || "");

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewName("");
  };

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const openEscalation = (item) => {
    setSelectedItem(item);
    setEscalationForm({
      resp1:            item.resp1            || "",
      resp1_email:      item.resp1_email      || "",
      resp2:            item.resp2            || "",
      resp2_email:      item.resp2_email      || "",
      resp3:            item.resp3            || "",
      resp3_email:      item.resp3_email      || "",
      rule_l1:          item.rule_l1          ?? 0,
      rule_l2:          item.rule_l2          ?? 3,
      rule_l3:          item.rule_l3          ?? 7,
      alertas_preaviso: item.alertas_preaviso || ["3", "1", "0"],
      channels:         item.channels         || ["email", "browser"],
    });
    setShowEscalation(true);
  };

  const saveEscalation = async () => {
    const f = escalationForm;
    if (!f.resp1.trim()) return toast.error(t("lr_esc_val_resp1_required"));
    if (f.resp1_email && !EMAIL_RE.test(f.resp1_email)) return toast.error(t("lr_esc_val_email1"));
    if (f.resp2_email && !EMAIL_RE.test(f.resp2_email)) return toast.error(t("lr_esc_val_email2"));
    if (f.resp3_email && !EMAIL_RE.test(f.resp3_email)) return toast.error(t("lr_esc_val_email3"));
    if (f.rule_l2 <= f.rule_l1) return toast.error(t("lr_esc_val_l2_gt_l1"));
    if (f.rule_l3 <= f.rule_l2) return toast.error(t("lr_esc_val_l3_gt_l2"));
    setEscalationSaving(true);
    try {
      await lrController.updateLegalRequirement(accessToken, selectedItem._id, f);
      setItems(prev => prev.map(i => i._id === selectedItem._id ? { ...i, ...f } : i));
      setShowEscalation(false);
      toast.success(t("lr_esc_saved"));
    } catch {
      toast.error(t("lr_esc_error_save"));
    } finally {
      setEscalationSaving(false);
    }
  };

  const openProgress = (item) => { setSelectedItem(item); setShowDates(true); };
  const openObs      = (item) => { setSelectedItem(item); setObsVal(""); setShowObs(true); };
  const openFiles    = (item) => { setSelectedItem(item); setShowFiles(true); };
  const openAttach   = (item) => { setSelectedItem(item); setShowAttach(true); };
  const openDelete   = (item) => { setSelectedItem(item); setShowConfirm(true); };

  const openPlan = (item) => {
    setSelectedItem(item);
    setLinkedPlan(null);
    setPlanTitle("");
    setShowPlan(true);
    actionPlanController.getActionPlanByLegalRequirement(accessToken, item._id)
      .then(plan => setLinkedPlan(plan || null))
      .catch(() => {});
  };

  const handleCreatePlan = async () => {
    if (creatingPlan || !selectedItem) return;
    const title = planTitle.trim() || `Requisito: ${selectedItem.requirement || "Sin nombre"}`;
    setCreatingPlan(true);
    try {
      const created = await actionPlanController.createActionPlan(accessToken, {
        title,
        findingOrigin:      "Requisito Legal",
        moduloOrigen:       "requisito_legal",
        requisitoLegalId:   selectedItem._id,
        findingDescription: selectedItem.requirement || "",
        site:               selectedItem.site?._id || selectedItem.site || undefined,
        status:             "pending",
      });
      setLinkedPlan(created.actionPlan);
      setPlanTitle("");
      toast.success("Plan de acción creado y vinculado al requisito legal");
    } catch (err) {
      toast.error(err?.msg || "No se pudo crear el plan de acción");
    } finally {
      setCreatingPlan(false);
    }
  };

  const handleDelete = async () => {
    try {
      await lrController.deleteLegalRequirement(accessToken, selectedItem._id);
      toast.success(t("legal_requirement_deleted"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_deleting")); }
  };

  const handleSaveProgress = async () => {
    setSaving(true);
    try {
      await lrController.updateLegalRequirement(accessToken, selectedItem._id, { progress: progressVal });
      toast.success(t("advance_saved"));
      setShowProgress(false);
      onReload();
    } catch { toast.error(t("error_saving_advance")); }
    finally { setSaving(false); }
  };

  const handleSaveObs = async () => {
    if (!obsVal.trim()) return;
    setSaving(true);
    try {
      const userName = [user?.name, user?.lastname].filter(Boolean).join(" ");
      const newObs = { text: obsVal.trim(), userName, createdAt: new Date() };
      const updatedObs = [...(selectedItem.observations || []), newObs];
      await lrController.updateLegalRequirement(accessToken, selectedItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setSelectedItem((prev) => ({ ...prev, observations: updatedObs }));
      setObsVal("");
      onReload();
    } catch { toast.error(t("error_saving_observations")); }
    finally { setSaving(false); }
  };

  const handleSaveObsEdit = async () => {
    if (!editingObsVal.trim()) return;
    setSaving(true);
    try {
      const updatedObs = (selectedItem.observations || []).map((o, i) =>
        i === editingObsIdx
          ? { ...o, text: editingObsVal.trim(), createdAt: editingObsDate ? new Date(editingObsDate) : o.createdAt }
          : o
      );
      await lrController.updateLegalRequirement(accessToken, selectedItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setSelectedItem((prev) => ({ ...prev, observations: updatedObs }));
      setEditingObsIdx(null);
      setEditingObsVal("");
      setEditingObsDate("");
    } catch { toast.error(t("error_saving_observations")); }
    finally { setSaving(false); }
  };

  const handleDeleteObs = async (origIdx) => {
    setSaving(true);
    try {
      const updatedObs = (selectedItem.observations || []).filter((_, i) => i !== origIdx);
      await lrController.updateLegalRequirement(accessToken, selectedItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setSelectedItem((prev) => ({ ...prev, observations: updatedObs }));
      onReload();
    } catch { toast.error(t("error_saving_observations")); }
    finally { setSaving(false); }
  };

  const renderEditRow = () => (
    <Table.Row key={editingId} positive={editingId === "new"} warning={editingId !== "new"}>
      {canTransfer && <Table.Cell />}
      <Table.Cell>
        <select
          value={editingData.area || ""}
          onChange={(e) => {
            if (e.target.value === "__add_area__") { setShowAreaModal(true); return; }
            const newArea = e.target.value;
            setEditingData((prev) => ({
              ...prev,
              area: newArea,
              requirement: "",
            }));
          }}
          className="lr-table__inline-select"
        >
          <option value="">{t("area")} *</option>
          {catAreas.map((o) => (
            <option key={o.key} value={o.value}>{o.text}</option>
          ))}
          <option value="__add_area__">+ {t("new_area")}</option>
        </select>
      </Table.Cell>
      <Table.Cell>
        <InlineSelect options={catSources} value={editingData.source} onChange={(v) => setField("source", v)} placeholder={t("source")} addLabel={`+ ${t("new_source")}`} onAdd={() => setShowSourceModal(true)} />
      </Table.Cell>
      <Table.Cell>
       <InlineSelect options={catReqs} value={editingData.requirement} onChange={(v) => setField("requirement", v)} placeholder={t("requirement") || "Requisito"} addLabel={`+ ${t("new_requirement") || "Nuevo requisito"}`} onAdd={() => setShowRequirementModal(true)} />
      </Table.Cell>
      <Table.Cell>
        <InlineSelect options={catAuthorities} value={editingData.authority} onChange={(v) => setField("authority", v)} placeholder={t("authority")} addLabel={`+ ${t("new_authority")}`} onAdd={() => setShowAuthorityModal(true)} />
      </Table.Cell>
      {/* Estado */}
      <Table.Cell>
        <InlineSelect
          options={[
            { key: "compliant",     value: "compliant",     text: t("status_cumple") },
            { key: "partial",       value: "partial",       text: t("status_parcial") },
            { key: "non_compliant", value: "non_compliant", text: t("status_no_cumple") },
            { key: "pending",       value: "pending",       text: t("status_pendiente") },
          ]}
          value={editingData.status}
          onChange={(v) => setField("status", v)}
          placeholder={t("estado")}
        />
      </Table.Cell>
      {/* Tipo de evidencia */}
      <Table.Cell>
        <EvidencePopup
          value={editingData.evidenceTypes || []}
          onChange={(vals) => setField("evidenceTypes", vals)}
        />
      </Table.Cell>
      {/* Frecuencia */}
      <Table.Cell>
        <InlineSelect
          options={catFrequencies}
          value={editingData.frequency}
          onChange={(v) => {
            const freqName = catFrequencies.find((f) => f.value === v)?.text || "";
            const startDateStr = editingData.occurrences?.[0]?.date || editingData.expiration?.split("T")[0];
            const newOccs = regenerateOccurrences(freqName, startDateStr, editingData.occurrences);
            setEditingData((prev) => ({
              ...prev,
              frequency: v,
              occurrences: newOccs.length ? newOccs : prev.occurrences,
            }));
          }}
          placeholder={t("frequency")}
          addLabel={`+ ${t("new_frequency")}`}
          onAdd={() => setShowFrequencyModal(true)}
        />
      </Table.Cell>
      {/* Fecha */}
      <Table.Cell>
        {editingData.occurrences?.length > 1 ? (
          <span style={{ fontSize: "0.82em", color: "#555", whiteSpace: "nowrap" }}>
            {formatDate(editingData.occurrences[0].date)} → {formatDate(editingData.occurrences[editingData.occurrences.length - 1].date)}
          </span>
        ) : editingData.occurrences?.length === 1 ? (
          <span style={{ fontSize: "0.85em", color: "#555" }}>
            {formatDate(editingData.occurrences[0].date)}
          </span>
        ) : editingId === "new" ? (
          <input
            type="date"
            value={editingData.expiration || ""}
            onChange={(e) => setField("expiration", e.target.value)}
            style={{ width: "100%", fontSize: "0.85em", padding: "2px 4px", border: "1px solid #ddd", borderRadius: 4 }}
          />
        ) : null}
      </Table.Cell>
      {/* Responsable */}
      <Table.Cell>
        <input
          type="text"
          value={editingData.responsible}
          onChange={(e) => setField("responsible", e.target.value)}
          placeholder={t("responsible")}
          className="lr-table__inline-input"
        />
      </Table.Cell>
      {/* Avance — automático según las fechas marcadas, no editable */}
      <Table.Cell>
        <span style={{ fontSize: "0.85em", fontWeight: 600 }}>{editingData.progress || 0}%</span>
      </Table.Cell>
      {/* Guardar / Cancelar */}
      <Table.Cell textAlign="center" className="lr-table__actions">
        <Popup size="mini" content={t("save")} trigger={
          <Button icon size="mini" loading={saving} disabled={saving} onClick={handleSaveEdit} className="lr-table__save-btn">
            <Icon name="save" />
          </Button>
        } />
        <Popup size="mini" content={t("cancel")} trigger={
          <Button icon size="mini" onClick={cancelEdit} disabled={saving}>
            <Icon name="close" />
          </Button>
        } />
      </Table.Cell>
    </Table.Row>
  );

  const renderQuickBar = () => (
    <div className="lr-table__quick-bar">
      <div className="lr-table__quick-fields">
        {/* Área */}
        <div className="lr-table__quick-field">
          <label>{t("area")} *</label>
          <FilterDropdown
            options={catAreas}
            value={editingData.area || ""}
            onChange={(v) => {
              setEditingData((prev) => ({ ...prev, area: v, requirement: "" }));
            }}
            placeholder={t("area")}
            addLabel={`+ ${t("new_area")}`}
            onAdd={() => setShowAreaModal(true)}
          />
        </div>

        {/* Norma */}
        <div className="lr-table__quick-field">
          <label>{t("source")}</label>
          <FilterDropdown
            options={catSources}
            value={editingData.source}
            onChange={(v) => setField("source", v)}
            placeholder={t("source")}
            addLabel={`+ ${t("new_source")}`}
            onAdd={() => setShowSourceModal(true)}
          />
        </div>

        {/* Requisito */}
        <div className="lr-table__quick-field">
          <label>{t("requirement") || "Requisito"}</label>
          <FilterDropdown
            options={catReqs}
            value={editingData.requirement}
            onChange={(v) => setField("requirement", v)}
            placeholder={t("requirement") || "Requisito"}
            addLabel={`+ ${t("new_requirement") || "Nuevo requisito"}`}
            onAdd={() => setShowRequirementModal(true)}
          />
        </div>

        {/* Autoridad */}
        <div className="lr-table__quick-field">
          <label>{t("authority")}</label>
          <FilterDropdown
            options={catAuthorities}
            value={editingData.authority}
            onChange={(v) => setField("authority", v)}
            placeholder={t("authority")}
            addLabel={`+ ${t("new_authority")}`}
            onAdd={() => setShowAuthorityModal(true)}
          />
        </div>

        {/* Estado */}
        <div className="lr-table__quick-field">
          <label>{t("estado")}</label>
          <FilterDropdown
            options={[
              { key: "compliant",     value: "compliant",     text: t("status_cumple") },
              { key: "partial",       value: "partial",       text: t("status_parcial") },
              { key: "non_compliant", value: "non_compliant", text: t("status_no_cumple") },
              { key: "pending",       value: "pending",       text: t("status_pendiente") },
            ]}
            value={editingData.status}
            onChange={(v) => setField("status", v)}
            placeholder={t("estado")}
          />
        </div>

        {/* Tipo de evidencia */}
        <div className="lr-table__quick-field">
          <label>{t("evidence_type")}</label>
          <EvidencePopup
            value={editingData.evidenceTypes || []}
            onChange={(vals) => setField("evidenceTypes", vals)}
          />
        </div>

        {/* Frecuencia */}
        <div className="lr-table__quick-field">
          <label>{t("frequency")}</label>
          <FilterDropdown
            options={catFrequencies}
            value={editingData.frequency}
            onChange={(v) => {
              const freqName = catFrequencies.find((f) => f.value === v)?.text || "";
              const startDateStr = editingData.occurrences?.[0]?.date || editingData.expiration?.split("T")[0];
              const newOccs = regenerateOccurrences(freqName, startDateStr, editingData.occurrences);
              setEditingData((prev) => ({
                ...prev,
                frequency: v,
                occurrences: newOccs.length ? newOccs : prev.occurrences,
              }));
            }}
            placeholder={t("frequency")}
            addLabel={`+ ${t("new_frequency")}`}
            onAdd={() => setShowFrequencyModal(true)}
          />
        </div>

        {/* Fecha */}
        <div className="lr-table__quick-field">
          <label>{t("expiration")}</label>
          {editingData.occurrences?.length > 1 ? (
            <span className="lr-table__quick-static">
              {formatDate(editingData.occurrences[0].date)} → {formatDate(editingData.occurrences[editingData.occurrences.length - 1].date)}
            </span>
          ) : editingData.occurrences?.length === 1 ? (
            <span className="lr-table__quick-static">
              {formatDate(editingData.occurrences[0].date)}
            </span>
          ) : (
            <input
              type="date"
              value={editingData.expiration || ""}
              onChange={(e) => setField("expiration", e.target.value)}
              className="lr-table__quick-input"
            />
          )}
        </div>

        {/* Responsable */}
        <div className="lr-table__quick-field">
          <label>{t("responsible")}</label>
          <input
            type="text"
            value={editingData.responsible}
            onChange={(e) => setField("responsible", e.target.value)}
            placeholder={t("responsible")}
            className="lr-table__quick-input"
          />
        </div>
      </div>

      <div className="lr-table__quick-actions">
        <Popup size="mini" content={t("save")} trigger={
          <Button icon size="mini" loading={saving} disabled={saving} onClick={handleSaveEdit} className="lr-table__save-btn">
            <Icon name="save" />
          </Button>
        } />
        <Popup size="mini" content={t("cancel")} trigger={
          <Button icon size="mini" onClick={cancelEdit} disabled={saving}>
            <Icon name="close" />
          </Button>
        } />
      </div>
    </div>
  );

  return (
    <div className="lr-table">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Acciones ── */}
      <div className="lr-action-row">
        <div className="lr-export-dropdown" ref={exportMenuRef}>
          <button className="lr-export-toggle" onClick={() => setShowExportMenu(v => !v)}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="13" height="13">
              <path d="M3 13h10M8 3v7m0 0L5 7m3 3 3-3"/>
            </svg>
            {t("export_btn")}
            <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9"><path d="M6 8L2 4h8z"/></svg>
          </button>
          {showExportMenu && (
            <div className="lr-export-menu">
              <button className="lr-export-menu-item" onClick={() => { handleExportCSV(); setShowExportMenu(false); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                CSV
              </button>
              <button className="lr-export-menu-item" onClick={() => { handleExportXLSX(); setShowExportMenu(false); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                Excel (XLSX)
              </button>
              <button className="lr-export-menu-item" onClick={() => { handleExportPDF(); setShowExportMenu(false); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                PDF
              </button>
            </div>
          )}
        </div>
        {canTransfer && (
          <button
            className="lr-export-toggle"
            onClick={openTransfer}
            disabled={selectedIds.size === 0}
            title={selectedIds.size === 0 ? t("lr_transfer_none_selected") : undefined}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="13" height="13">
              <path d="M5 4h7l-2-2m2 2-2 2M11 12H4l2 2m-2-2 2-2"/>
            </svg>
            {t("lr_transfer_button")}{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
          </button>
        )}
        {!noSiteInCtx && (
          <button className="lr-new-btn" onClick={startNew} title={t("new_requirement")}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
              <path d="M8 3v10M3 8h10"/>
            </svg>
          </button>
        )}
      </div>

      {/* ── Filtros + tabla, en una sola card ── */}
      <div className="lr-table__card">
        <div className="lr-filters">
          <div className="lr-filter-block lr-filter-search">
            <div className="lr-search-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                className="lr-filter-input"
                placeholder={t("lr_search_placeholder")}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          {noSiteInCtx && sites.length > 0 && (
            <div className="lr-filter-block">
              <FilterDropdown
                options={sites.map(s => ({ key: s._id, value: s._id, text: s.name }))}
                value={siteFilter}
                onChange={setSiteFilter}
                placeholder={t("site")}
              />
            </div>
          )}
          <div className="lr-filter-block">
            <FilterDropdown
              options={STATUS_FILTER_OPTIONS.filter(o => o.value)}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder={t("estado")}
            />
          </div>
          <div className="lr-filter-block">
            <FilterDropdown
              options={catAreas}
              value={areaFilter}
              onChange={setAreaFilter}
              placeholder={t("area")}
            />
          </div>
        </div>

        {/* Barra rápida de creación — visible arriba de la tabla al cargar un nuevo requisito */}
        {editingId === "new" && (
          <>
            {renderQuickBar()}
            <div className="lr-table__quick-dates">
              <LegalRequirementDates
                key={editingData.frequency}
                hideTitle
                item={{
                  frequency: catFrequencies.find(f => f.value === editingData.frequency)
                    ? { name: catFrequencies.find(f => f.value === editingData.frequency).text }
                    : null,
                  occurrences: editingData.occurrences || [],
                  expiration: editingData.expiration,
                }}
                close={cancelEdit}
                onLocalSave={(occs, prog) => {
                  setField("occurrences", occs);
                  setField("progress", prog);
                }}
              />
            </div>
          </>
        )}

        {/* ── Tabla ── */}
        {items === null ? (
          <LoadingSpinner />
        ) : itemsFilter.length === 0 && editingId !== "new" ? (
          <div className="lr-table__empty">{t("no_results")}</div>
        ) : (
          <div className="lr-table__tbl-scroll">
          <Table className="lr-table__tbl unstackable rtable-cards">
            <Table.Header>
              <Table.Row>
                {canTransfer && (
                  <Table.HeaderCell textAlign="center">
                    <input
                      type="checkbox"
                      checked={itemsFilter.length > 0 && selectedIds.size === itemsFilter.length}
                      onChange={toggleSelectAll}
                    />
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell>{t("area")}</Table.HeaderCell>
                <Table.HeaderCell>{t("source")}</Table.HeaderCell>
                <Table.HeaderCell>{t("requirement") || "Requisito"}</Table.HeaderCell>
                <Table.HeaderCell>{t("authority")}</Table.HeaderCell>
                <Table.HeaderCell>{t("estado")}</Table.HeaderCell>
                <Table.HeaderCell>{t("evidence_type")}</Table.HeaderCell>
                <Table.HeaderCell>{t("frequency")}</Table.HeaderCell>
                <Table.HeaderCell>{t("expiration")}</Table.HeaderCell>
                <Table.HeaderCell>{t("responsible")}</Table.HeaderCell>
                <Table.HeaderCell>{t("advance")}</Table.HeaderCell>
                <Table.HeaderCell textAlign="center"></Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {itemsFilter.map((item) =>
                  editingId === item._id ? (
                    <React.Fragment key={item._id}>
                      {renderEditRow()}
                      <Table.Row className="lr-table__dates-row">
                        <Table.Cell colSpan={totalCols}>
                          <LegalRequirementDates
                            key={editingData.frequency}
                            hideTitle
                            item={{
                              frequency: catFrequencies.find(f => f.value === editingData.frequency)
                                ? { name: catFrequencies.find(f => f.value === editingData.frequency).text }
                                : item.frequency,
                              occurrences: editingData.occurrences?.length ? editingData.occurrences : (item.occurrences || []),
                              expiration: editingData.expiration || item.expiration,
                            }}
                            close={cancelEdit}
                            onReload={onReload}
                            onLocalSave={(occs, prog) => {
                              setField("occurrences", occs);
                              setField("progress", prog);
                            }}
                          />
                        </Table.Cell>
                      </Table.Row>
                    </React.Fragment>
                  ) : (
                    <Table.Row key={item._id}>
                      {canTransfer && (
                        <Table.Cell textAlign="center" data-label="">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(item._id)}
                            onChange={() => toggleSelected(item._id)}
                          />
                        </Table.Cell>
                      )}
                      <Table.Cell data-label={t("area")}>{item.area?.name          || "-"}</Table.Cell>
                      <Table.Cell data-label={t("source")}>{item.source?.name        || "-"}</Table.Cell>
                      <Table.Cell data-label={t("requirement") || "Requisito"}>{item.requirement?.name   || "-"}</Table.Cell>
                      <Table.Cell data-label={t("authority")}>{item.authority?.name     || "-"}</Table.Cell>
                      <Table.Cell data-label={t("estado")}>
                        {item.status
                          ? <Label size="small" className="lr-table__status-badge" color={STATUS_COLOR[item.status] || "grey"}>
                              {STATUS_FILTER_OPTIONS.find((o) => o.value === item.status)?.text || item.status}
                            </Label>
                          : "-"}
                      </Table.Cell>
                      <Table.Cell data-label={t("evidence_type")}>
                        {item.evidenceTypes?.length
                          ? item.evidenceTypes.map((v) => (
                              <Label key={v} size="mini" style={{ margin: "1px 2px" }}>
                                {EVIDENCE_TYPE_OPTIONS.find((o) => o.value === v)?.text || v}
                              </Label>
                            ))
                          : "-"}
                      </Table.Cell>
                      <Table.Cell data-label={t("frequency")}>{item.frequency?.name   || "-"}</Table.Cell>
                      <Table.Cell data-label={t("expiration")}>
                        {(() => {
                          const alert = getAlertLevel(item);
                          const color = alert === "expired" ? "#e53935" : alert === "warning" ? "#f57c00" : "#555";
                          const icon  = alert === "expired" ? "exclamation circle" : alert === "warning" ? "exclamation triangle" : null;
                          const tip   = alert === "expired" ? "Vencido" : alert === "warning" ? "Vence en menos de 30 días" : null;
                          const dateStr = item.occurrences?.length > 1
                            ? `${formatDate(item.occurrences[0].date)} → ${formatDate(item.occurrences[item.occurrences.length - 1].date)}`
                            : formatDate(item.occurrences?.[0]?.date || item.expiration);
                          const inner = (
                            <span style={{ fontSize: "0.82em", color, fontWeight: alert ? 600 : "inherit", whiteSpace: "nowrap" }}>
                              {dateStr}{icon && <Icon name={icon} style={{ marginLeft: 4 }} />}
                            </span>
                          );
                          return tip ? <Popup size="mini" content={tip} trigger={inner} /> : inner;
                        })()}
                      </Table.Cell>
                      <Table.Cell data-label={t("responsible")}>{item.responsible || "-"}</Table.Cell>
                      <Table.Cell data-label={t("advance")}>
                        <span style={{ fontSize: "0.85em", fontWeight: 600 }}>{item.progress || 0}%</span>
                      </Table.Cell>

                      {/* Acciones */}
                      <Table.Cell textAlign="center" className="lr-table__actions" onClick={(e) => e.stopPropagation()}>
                        <div className="lr-table__row-menu">
                          <button
                            type="button"
                            className="lr-table__row-menu-btn"
                            onClick={(e) => toggleRowMenu(item._id, e)}
                          >
                            <Icon name="ellipsis vertical" />
                          </button>
                        </div>
                        {openRowMenu?.id === item._id && ReactDOM.createPortal(
                          <div
                            className="lr-table__row-menu-list"
                            style={{
                              position: "fixed",
                              top: openRowMenu.top,
                              left: openRowMenu.left,
                              transform: openRowMenu.openUp ? "translateY(-100%)" : "none",
                            }}
                          >
                            <button onClick={() => { startEdit(item); setOpenRowMenu(null); }} disabled={editingId !== null}>
                              <Icon name="pencil" />{t("edit")}
                            </button>
                            <button onClick={() => { openProgress(item); setOpenRowMenu(null); }}>
                              <Icon name="chart line" />{t("register_advance")}
                            </button>
                            <button onClick={() => { openObs(item); setOpenRowMenu(null); }}>
                              <Icon name="comment outline" />{t("observations")}
                            </button>
                            <button onClick={() => { openAttach(item); setOpenRowMenu(null); }}>
                              <Icon name="attach" />{t("attach_files")}
                            </button>
                            <button onClick={() => { openFiles(item); setOpenRowMenu(null); }}>
                              <Icon name="folder open outline" />{t("view_files")}
                            </button>
                            <button onClick={() => { openPlan(item); setOpenRowMenu(null); }}>
                              <Icon name="tasks" />Plan de acción
                            </button>
                            <button onClick={() => { openEscalation(item); setOpenRowMenu(null); }}>
                              <Icon name="bell outline" />Escalación
                            </button>
                            <button className="lr-table__row-menu-item--danger" onClick={() => { openDelete(item); setOpenRowMenu(null); }} disabled={editingId !== null}>
                              <Icon name="trash" />{t("delete")}
                            </button>
                          </div>,
                          document.body
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )
                )
              }
            </Table.Body>
          </Table>
          </div>
        )}
      </div>

      {/* ── Modal Fechas (Registrar Avance) ── */}
      {showDates && ReactDOM.createPortal(
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.35)", backdropFilter: "blur(2px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowDates(false); }}>
          <div style={{ background: "#F8F9FB", borderRadius: "20px", padding: "30px 32px 26px", width: "min(700px, 95vw)", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>
            <p style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1A2E4A", fontFamily: "'Archivo', sans-serif", margin: "0 0 20px" }}>{t("register_advance")}</p>
            {selectedItem && (
              <LegalRequirementDates
                item={selectedItem}
                close={() => { setShowDates(false); }}
                onReload={onReload}
                showFooter
                hideTitle
              />
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ── Modal Avance ── */}
      <LRCatalogModal open={showProgress} onClose={() => setShowProgress(false)} title={t("register_advance")}>
        <div style={{ marginBottom: "1.2rem" }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: "0.35rem", fontSize: "0.875rem", color: "#374151" }}>
            {t("advance_percent")}
          </label>
          <div style={{ height: "8px", borderRadius: "4px", background: "#E5E7EB", marginBottom: "0.75rem" }}>
            <div style={{ height: "100%", width: `${progressVal}%`, borderRadius: "4px", background: "#4A7FA5", transition: "width 0.2s" }} />
          </div>
          <input
            type="range" min={0} max={100} value={progressVal}
            onChange={(e) => setProgressVal(Number(e.target.value))}
            style={{ width: "100%", marginBottom: "0.75rem", cursor: "pointer", accentColor: "#4A7FA5" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <input
              type="number" min={0} max={100} value={progressVal}
              onChange={(e) => setProgressVal(Math.min(100, Math.max(0, Number(e.target.value))))}
              style={{ width: "70px", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "0.95rem", textAlign: "center", outline: "none", fontFamily: "'Archivo', sans-serif", boxSizing: "border-box" }}
            />
            <span style={{ fontWeight: 600, color: "#374151", fontSize: "0.95rem" }}>%</span>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: "14px", marginTop: "2rem" }}>
          <button
            type="button"
            disabled={saving}
            onClick={handleSaveProgress}
            style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: saving ? "not-allowed" : "pointer", background: "#4A7FA5", color: "#fff", opacity: saving ? 0.55 : 1 }}
          >
            {saving ? "…" : t("save")}
          </button>
          <button
            type="button"
            onClick={() => setShowProgress(false)}
            style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", background: "#9BAFC4", color: "#fff" }}
          >
            {t("cancel")}
          </button>
        </div>
      </LRCatalogModal>

      {/* ── Modal Observaciones ── */}
      {showObs && (
        <div className="lr-obs-backdrop" onClick={e => { if (e.target === e.currentTarget) { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); } }}>
          <div className="lr-obs-modal">
            <div className="lr-obs-header">
              <h3 className="lr-obs-title">{t("observations")}</h3>
              <button className="lr-obs-close" type="button" onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="lr-obs-body">
        <div className="lr-table__modal-inner">
          {(selectedItem?.observations?.length > 0) && (
            <div className="lr-table__obs-history">
              {[...(selectedItem.observations)].map((obs, origIdx) => ({ obs, origIdx })).reverse().map(({ obs, origIdx }) => {
                const currentUser = [user?.name, user?.lastname].filter(Boolean).join(" ");
                const isOwner = obs.userName === currentUser;
                const isEditing = editingObsIdx === origIdx;
                return (
                  <div key={origIdx} className="lr-table__obs-entry">
                    <span
                      className="lr-table__obs-avatar"
                      style={{ background: avatarColor(obs.userName) }}
                    >
                      {getInitials(obs.userName)}
                    </span>
                    <div className="lr-table__obs-body">
                      <div className="lr-table__obs-meta">
                        <span className="lr-table__obs-user">{obs.userName}</span>
                        <span className="lr-table__obs-date">
                          {obs.createdAt ? new Date(obs.createdAt).toLocaleDateString() : ""}
                        </span>
                        {isOwner && !isEditing && (
                          <span className="lr-table__obs-actions">
                            <Icon name="pencil" link onClick={() => {
                              setEditingObsIdx(origIdx);
                              setEditingObsVal(obs.text);
                              setEditingObsDate(obs.createdAt ? new Date(obs.createdAt).toISOString().split("T")[0] : "");
                            }} />
                            <Icon name="trash alternate" link color="red" onClick={() => handleDeleteObs(origIdx)} />
                          </span>
                        )}
                      </div>
                      {isEditing ? (
                        <div>
                          <TextArea
                            rows={2}
                            value={editingObsVal}
                            onChange={(e) => setEditingObsVal(e.target.value)}
                            style={{ width: "100%", marginBottom: "0.5rem" }}
                          />
                          <div style={{ display: "flex", gap: "0.4rem" }}>
                            <button type="button" disabled={saving || !editingObsVal.trim()} onClick={handleSaveObsEdit} className="obs-icon-btn obs-icon-btn--confirm">
                              <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 6.5L5 9.5L11 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                            <button type="button" onClick={() => { setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }} className="obs-icon-btn obs-icon-btn--cancel">
                              <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M8.5 2.5L2.5 8.5M2.5 2.5L8.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="lr-table__obs-text">{obs.text}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <TextArea
            rows={3}
            value={obsVal}
            onChange={(e) => setObsVal(e.target.value)}
            placeholder={t("enter_observations")}
            style={{ width: "100%", marginTop: "0.75rem" }}
          />
          <div className="lr-obs-actions">
            <button className="lr-obs-btn-confirm" disabled={saving || !obsVal.trim()} onClick={handleSaveObs}>
              {saving ? "…" : t("save")}
            </button>
            <button className="lr-obs-btn-cancel" onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }}>{t("cancel")}</button>
          </div>
        </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Adjuntar Archivos ── */}
      {showAttach && (
        <div className="lr-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowAttach(false); }}>
          <div className="lr-obs-modal">
            <div className="lr-obs-header">
              <h3 className="lr-obs-title">{t("attach_files")}</h3>
              <button className="lr-obs-close" type="button" onClick={() => setShowAttach(false)}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="lr-obs-body">
              <FileDropzone
                onUpload={handleUpload}
                uploading={uploading}
                onCancel={() => setShowAttach(false)}
                supportedLabel="Soportados: PDF, Imágenes (JPG/PNG/WebP), CSV, XLSX/XLS, TXT"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Transferir a otro año ── */}
      {showTransfer && (
        <div className="lr-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowTransfer(false); }}>
          <div className="lr-obs-modal">
            <div className="lr-obs-header">
              <h3 className="lr-obs-title">{t("lr_transfer_title")}</h3>
              <button className="lr-obs-close" type="button" onClick={() => setShowTransfer(false)}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="lr-obs-body">
              <p className="lr-obs-transfer-text">
                {t("lr_transfer_confirm_prefix")} {selectedIds.size} {t("lr_transfer_confirm_middle")}{" "}
                <select
                  className="lr-obs-transfer-select"
                  value={transferYear}
                  onChange={(e) => setTransferYear(Number(e.target.value))}
                >
                  {[1, 2, 3].map((offset) => {
                    const y = (extYear || new Date().getFullYear()) + offset;
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </select>
                . {t("lr_transfer_confirm_suffix")}
              </p>
            </div>
            <div className="lr-obs-actions">
              <button className="lr-obs-btn-confirm" disabled={transferring} onClick={handleTransfer}>
                {transferring ? "…" : t("lr_transfer_confirm_btn")}
              </button>
              <button className="lr-obs-btn-cancel" onClick={() => setShowTransfer(false)}>{t("cancel")}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Visualizar Archivos ── */}
      {showFiles && (
        <div className="lr-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowFiles(false); }}>
          <div className="lr-obs-modal">
            <div className="lr-obs-header">
              <h3 className="lr-obs-title">{t("attached_files")}</h3>
              <button className="lr-obs-close" type="button" onClick={() => setShowFiles(false)}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="lr-obs-body">
              {!selectedItem?.files?.length ? (
                <p style={{ color: "#999", textAlign: "center", padding: "1.5rem 0", margin: 0 }}>{t("no_attached_files")}</p>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px" }}>
                  {selectedItem.files.map((f, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "10px 12px", borderRadius: "10px", background: "#fff", border: "1px solid #E5E7EB", marginBottom: "8px" }}>
                      <Icon name={isImage(f.name) ? "image outline" : "file outline"} style={{ color: "#4A7FA5", margin: 0, fontSize: "1.1rem" }} />
                      <span style={{ flex: 1, fontSize: "0.875rem", color: "#374151", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</span>
                      <button title={t("view")} onClick={() => handleViewFile(f.key, f.name)}
                        style={{ border: "none", background: "#4A7FA5", color: "#fff", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="eye" style={{ margin: 0, fontSize: "0.8rem" }} />
                      </button>
                      <EvidenceTextButton module="legalRequirement" fileKey={f.key} fileName={f.name} />
                      <button title={t("delete")} onClick={() => handleDeleteFile(f.key)} disabled={deletingFile !== null}
                        style={{ border: "none", background: deletingFile === f.key ? "#ccc" : "#E74C3C", color: "#fff", borderRadius: "50%", width: "30px", height: "30px", cursor: deletingFile !== null ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="trash" style={{ margin: 0, fontSize: "0.8rem" }} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button onClick={() => setShowFiles(false)}
                  style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", background: "#9BAFC4", color: "#fff" }}>
                  {t("close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Preview Imagen ── */}
      <LRCatalogModal open={!!previewUrl} onClose={closePreview} title={previewName}>
        {previewUrl && (
          <div style={{ textAlign: "center", background: "#1a1a1a", borderRadius: "8px", padding: "1rem", marginBottom: "1rem" }}>
            <img
              src={previewUrl}
              alt={previewName}
              style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }}
            />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            type="button"
            onClick={closePreview}
            style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", background: "#9BAFC4", color: "#fff" }}
          >
            {t("close")}
          </button>
        </div>
      </LRCatalogModal>

      {/* ── Modal Plan de Acción ── */}
      <LRCatalogModal
        open={showPlan}
        onClose={() => { setShowPlan(false); setLinkedPlan(null); setPlanTitle(""); }}
        title={t("linked_action_plan")}
      >
        <div className="lr-table__modal-inner">
          {!linkedPlan ? (
            <>
              <p style={{ color: "#666", marginBottom: "1rem" }}>
                {t("no_linked_plan")}
              </p>
              <input
                type="text"
                placeholder={`${t("requirement")}: ${selectedItem?.requirement || t("no_name")}`}
                value={planTitle}
                onChange={(e) => setPlanTitle(e.target.value)}
                style={{
                  width: "100%", padding: "10px 14px", border: "1px solid #D1D5DB",
                  borderRadius: "6px", fontSize: "0.95rem", outline: "none",
                  boxSizing: "border-box", fontFamily: "'Archivo', sans-serif",
                }}
              />
              <div style={{ display: "flex", justifyContent: "center", gap: "12px", marginTop: "1.5rem" }}>
                <button
                  type="button"
                  disabled={creatingPlan}
                  onClick={handleCreatePlan}
                  style={{
                    border: "none", borderRadius: "20px", padding: "10px 30px",
                    fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif",
                    cursor: creatingPlan ? "not-allowed" : "pointer",
                    background: "#4A7FA5", color: "#fff", opacity: creatingPlan ? 0.55 : 1,
                  }}
                >
                  {creatingPlan ? "…" : `+ ${t("create_action_plan")}`}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowPlan(false); setLinkedPlan(null); setPlanTitle(""); }}
                  style={{
                    border: "none", borderRadius: "20px", padding: "10px 30px",
                    fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif",
                    cursor: "pointer", background: "#9BAFC4", color: "#fff",
                  }}
                >
                  {t("cancel")}
                </button>
              </div>
            </>
          ) : (
            <>
              <p style={{ color: "#555", fontSize: "0.85rem", marginBottom: "0.75rem" }}>
                {t("lr_manage_plan_hint")}
              </p>
              <div style={{ background: "#f9f9f9", border: "1px solid #e0e0e0", borderRadius: 6, padding: "1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                  <strong style={{ fontSize: "0.95rem" }}>{linkedPlan.title}</strong>
                  <Label
                    size="tiny"
                    color={
                      linkedPlan.status === "closed"               ? "green"  :
                      linkedPlan.status === "in_progress"          ? "blue"   :
                      linkedPlan.status === "implemented_pending"  ? "teal"   :
                      linkedPlan.status === "overdue"              ? "red"    :
                      linkedPlan.status === "cancelled"            ? "grey"   : "orange"
                    }
                  >
                    {t(`ap_status_${linkedPlan.status}`) || linkedPlan.status}
                  </Label>
                </div>
                <div style={{ display: "flex", gap: "1.5rem", fontSize: "0.82rem", color: "#666", marginBottom: "0.6rem" }}>
                  <span>
                    {t("priority")}:{" "}
                    <strong style={{ color: linkedPlan.priority === "high" ? "#c0392b" : linkedPlan.priority === "medium" ? "#e67e22" : "#27ae60" }}>
                      {t(`priority_${linkedPlan.priority}_short`) || linkedPlan.priority}
                    </strong>
                  </span>
                  {linkedPlan.responsible && <span>{t("responsible")}: <strong>{linkedPlan.responsible}</strong></span>}
                  {linkedPlan.dueDate && (
                    <span style={{ color: new Date(linkedPlan.dueDate) < new Date() && linkedPlan.status !== "closed" ? "#c0392b" : "inherit" }}>
                      {t("lr_expires")}: <strong>{new Date(linkedPlan.dueDate).toLocaleDateString("es-AR")}</strong>
                      {new Date(linkedPlan.dueDate) < new Date() && linkedPlan.status !== "closed" && " ⚠️"}
                    </span>
                  )}
                </div>
                <Progress
                  percent={linkedPlan.progress || 0}
                  size="tiny"
                  color={linkedPlan.progress >= 100 ? "green" : linkedPlan.progress > 0 ? "blue" : "grey"}
                  style={{ marginBottom: 0 }}
                  label={`${linkedPlan.progress || 0}%`}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
                <button
                  type="button"
                  onClick={() => { setShowPlan(false); setLinkedPlan(null); setPlanTitle(""); }}
                  style={{
                    border: "none", borderRadius: "20px", padding: "10px 30px",
                    fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif",
                    cursor: "pointer", background: "#9BAFC4", color: "#fff",
                  }}
                >
                  {t("close")}
                </button>
              </div>
            </>
          )}
        </div>
      </LRCatalogModal>

      {/* ── Escalación ── */}
      {showEscalation && escalationForm && (
        <LRCatalogModal open={showEscalation} onClose={() => setShowEscalation(false)} title={t("lr_esc_title")}>
          <div className="lr-esc">
            {[1, 2, 3].map((lvl) => {
              const nameKey  = `resp${lvl}`;
              const emailKey = `resp${lvl}_email`;
              const isOptional = lvl > 1;
              const roleLabel = lvl === 1 ? t("lr_esc_resp1") : lvl === 2 ? t("lr_esc_resp2") : t("lr_esc_resp3");
              return (
                <div key={lvl} className="lr-esc__level">
                  <div className="lr-esc__level-badge" style={{ background: lvl === 1 ? "#1A5FA0" : lvl === 2 ? "#3D6B9E" : "#648EBC" }}>
                    {lvl}
                  </div>
                  <div className="lr-esc__level-fields">
                    <label className="lr-esc__label">
                      {t("lr_esc_level_label")} {lvl} — {roleLabel}
                      {isOptional && <span className="lr-esc__optional"> {t("lr_esc_optional")}</span>}
                    </label>
                    <div className="lr-esc__row">
                      <input
                        className="lr-esc__input"
                        placeholder={t("lr_esc_name_placeholder")}
                        value={escalationForm[nameKey]}
                        onChange={e => setEscalationForm(f => ({ ...f, [nameKey]: e.target.value }))}
                      />
                      <input
                        className="lr-esc__input"
                        placeholder="Email"
                        type="email"
                        value={escalationForm[emailKey]}
                        onChange={e => setEscalationForm(f => ({ ...f, [emailKey]: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="lr-esc__rules">
              <label className="lr-esc__label">{t("lr_esc_rules_label")}</label>
              <div className="lr-esc__rules-row">
                {["rule_l1", "rule_l2", "rule_l3"].map((key, i) => (
                  <label key={key} className="lr-esc__rule-item">
                    <span>L{i + 1}</span>
                    <input
                      type="number" min="0" max="90"
                      className="lr-esc__rule-input"
                      value={escalationForm[key]}
                      onChange={e => setEscalationForm(f => ({ ...f, [key]: Number(e.target.value) }))}
                    />
                    <span>{t("lr_esc_days")}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="lr-esc__preaviso">
              <label className="lr-esc__label">{t("lr_esc_preaviso_label")}</label>
              <div className="lr-esc__pills">
                {["7", "5", "3", "1", "0"].map(d => {
                  const active = escalationForm.alertas_preaviso.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      className={`lr-esc__pill${active ? " lr-esc__pill--active" : ""}`}
                      onClick={() => setEscalationForm(f => ({
                        ...f,
                        alertas_preaviso: active
                          ? f.alertas_preaviso.filter(x => x !== d)
                          : [...f.alertas_preaviso, d],
                      }))}
                    >
                      {d === "0" ? t("lr_esc_due") : `${d}d`}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="lr-esc__channels">
              <label className="lr-esc__label">{t("lr_esc_channels_label")}</label>
              <div className="lr-esc__pills">
                {[["email", "Email"], ["browser", "Browser"]].map(([val, label]) => {
                  const active = escalationForm.channels.includes(val);
                  return (
                    <button
                      key={val}
                      type="button"
                      className={`lr-esc__pill${active ? " lr-esc__pill--active" : ""}`}
                      onClick={() => setEscalationForm(f => ({
                        ...f,
                        channels: active
                          ? f.channels.filter(x => x !== val)
                          : [...f.channels, val],
                      }))}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="lr-esc__footer">
              <button
                type="button"
                className="lr-esc__save-btn"
                disabled={escalationSaving}
                onClick={saveEscalation}
              >
                {escalationSaving ? t("lr_esc_saving") : t("lr_esc_save")}
              </button>
              <button
                type="button"
                className="lr-esc__cancel-btn"
                onClick={() => setShowEscalation(false)}
              >
                {t("lr_esc_cancel")}
              </button>
            </div>
          </div>
        </LRCatalogModal>
      )}

      {/* ── Confirm Delete ── */}
      <ConfirmDialog
        open={showConfirm}
        title={t("delete")}
        message={t("confirm_delete_legal_requirement")}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleDelete}
        confirmLabel={t("accept")}
        cancelLabel={t("cancel")}
        confirmDanger
      />


      {/* ── Modal Nueva Autoridad ── */}
      <LRCatalogModal open={showAuthorityModal} onClose={() => setShowAuthorityModal(false)} title={t("new_authority")}>
        <AuthorityForm
          authority={null}
          close={() => setShowAuthorityModal(false)}
          onReload={() => reloadAuthorities(true)}
        />
      </LRCatalogModal>

      {/* ── Modal Nueva Área ── */}
      <LRCatalogModal open={showAreaModal} onClose={() => setShowAreaModal(false)} title={t("new_area")}>
        <AreaForm
          area={null}
          close={() => setShowAreaModal(false)}
          onReload={() => reloadAreas(true)}
        />
      </LRCatalogModal>

      {/* ── Modal Nueva Fuente ── */}
      <LRCatalogModal open={showSourceModal} onClose={() => setShowSourceModal(false)} title={t("new_source")}>
        <SourceForm
          source={null}
          close={() => setShowSourceModal(false)}
          onReload={() => reloadSources(true)}
        />
      </LRCatalogModal>
      {/* ── Modal Nuevo Requisito ── */}
  <LRCatalogModal open={showRequirementModal} onClose={() => setShowRequirementModal(false)} title={t("new_requirement") || "Nuevo Requisito"}>
    <RequirementForm
      requirement={null}
      close={() => setShowRequirementModal(false)}
      onReload={() => reloadRequirements(true)}
    />
  </LRCatalogModal>


      {/* ── Modal Nueva Frecuencia ── */}
      <LRCatalogModal open={showFrequencyModal} onClose={() => setShowFrequencyModal(false)} title={t("new_frequency")}>
        <FrequencyForm
          frequency={null}
          close={() => setShowFrequencyModal(false)}
          onReload={() => reloadFrequencies(true)}
        />
      </LRCatalogModal>
    </div>
  );
}
